import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, IconButton, Stack, Typography, Box, CircularProgress, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { translate as t } from 'hooks/useTranslations';
import useStickyState from 'hooks/useStickyState';
import { HeaderConfigurationResponse, PolicyHeaderFieldType, PolicyHeaderIssuingAuthorityType } from 'views/App/Portal/Policy/_types/manuals/draft';
import PropertyList, { PropertyListItem } from 'components/_layouts/PropertyList';
import useManageAttachment from '../../_data_sources/useManageAttachment';
import { formatDate } from 'views/App/Portal/Policy/_helpers/dateFormatHelper';
import brokenImage from 'components/ContentEditor/images/image-broken.svg';
import { Attachment } from 'views/App/Portal/Policy/_types/manuals/draft';

// Type for headers from preface data
type PolicyHeader = {
  type: PolicyHeaderFieldType;
  dateFormat?: string;
  autoSetDate?: boolean;
  agencyLogo?: string;
  issuingAuthorityType?: PolicyHeaderIssuingAuthorityType;
  issuingAuthoritySignature?: string;
  issuingAuthorityName?: string;
  header?: string;
  value?: string;
  state?: 'ENABLED' | 'DISABLED';
};

type PolicyHeadersProps = {
  manualHeaders?: HeaderConfigurationResponse;
  policyHeaders?: PolicyHeader[];
  manualId: string;
  readOnly?: boolean;
  translationPrefix?: 'stagedManuals' | 'issuedManuals';
};

const IMAGE_CONTAINER_SX = {
  height: '60px',
  minWidth: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const IMAGE_STYLE = {
  maxHeight: '60px',
  maxWidth: '200px',
  objectFit: 'contain' as const,
};

const getAttachmentKey = (type: 'agencyLogo' | 'signature', id: string): string => {
  return `${type}_${id}`;
};

const PolicyHeaders: React.FC<PolicyHeadersProps> = ({
  manualHeaders,
  policyHeaders,
  manualId,
  translationPrefix = 'stagedManuals',
}: PolicyHeadersProps) => {
  const [expanded, setExpanded] = useStickyState<boolean>('policy-header-display-expanded', false);
  const collapsed = !expanded;

  const { getAttachment: fetchAttachment } = useManageAttachment();
  const [attachments, setAttachments] = useState<Record<string, Attachment>>({});
  const [loadingAttachments, setLoadingAttachments] = useState<Record<string, boolean>>({});
  const [errorAttachments, setErrorAttachments] = useState<Record<string, boolean>>({});
  const fetchedAttachmentsRef = useRef<Set<string>>(new Set());

  const getHeaderLabel = (key: string): string => {
    return t(`policy.${translationPrefix}.policyHeaders.${key}`);
  };

  const renderAttachmentImage = (
    attachmentKey: string,
    imageUrl: string,
    altText: string
  ): React.ReactNode => {
    const isLoading = loadingAttachments[attachmentKey];
    const hasError = errorAttachments[attachmentKey];

    return (
      <Box sx={IMAGE_CONTAINER_SX}>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : hasError ? (
          <img
            src={brokenImage}
            alt="Failed to load"
            style={{
              height: '60px',
              width: '100%',
              opacity: 0.2,
            }}
          />
        ) : (
          <img
            src={imageUrl}
            alt={altText}
            style={IMAGE_STYLE}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              setErrorAttachments((prev) => ({ ...prev, [attachmentKey]: true }));
            }}
          />
        )}
      </Box>
    );
  };

  const renderDateHeader = (type: 'effectiveDate' | 'revisedDate', value?: string, dateFormat?: string, autoSet?: boolean) => {
    let displayValue: string;

    if (value) {
      displayValue = formatDate(value, dateFormat);
    } else if (autoSet === true) {
      displayValue = '(autofill)';
    } else {
      displayValue = '--';
    }

    headerItems.push({
      label: getHeaderLabel(type),
      value: displayValue,
    });
  };

  // Fetch attachments when manualHeaders or headers change
  useEffect(() => {
    if (!manualId) return;

    const attachmentIdsToFetch: Array<{ id: string; key: string; }> = [];

    // Helper to queue attachment for fetching if not already attempted
    const queueAttachment = (attachmentId: string | undefined, keyPrefix: 'agencyLogo' | 'signature') => {
      if (!attachmentId) return;

      const key = getAttachmentKey(keyPrefix, attachmentId);
      if (!fetchedAttachmentsRef.current.has(key)) {
        attachmentIdsToFetch.push({ id: attachmentId, key });
        fetchedAttachmentsRef.current.add(key);
      }
    };

    // Check manualHeaders first
    if (manualHeaders && manualHeaders.state === 'ENABLED') {
      if (manualHeaders.agencyLogo) {
        queueAttachment(manualHeaders.agencyLogo, 'agencyLogo');
      }
      if (manualHeaders.issuingAuthoritySignature) {
        queueAttachment(manualHeaders.issuingAuthoritySignature, 'signature');
      }
    }

    // Check policyHeaders
    if (policyHeaders) {
      policyHeaders.forEach((header) => {
        if (header.state === 'DISABLED') return;
        if (header.type === 'AGENCY_LOGO' && header.agencyLogo) {
          queueAttachment(header.agencyLogo, 'agencyLogo');
        } else if (header.type === 'ISSUING_AUTHORITY' && header.issuingAuthoritySignature) {
          queueAttachment(header.issuingAuthoritySignature, 'signature');
        }
      });
    }

    // Fetch all attachments that need to be fetched
    attachmentIdsToFetch.forEach(({ id, key }) => {
      setLoadingAttachments((prev) => ({ ...prev, [key]: true }));

      fetchAttachment({ manualId, attachmentId: id })
        .then((response) => {
          setAttachments((prev) => ({ ...prev, [key]: response as any }));
          setLoadingAttachments((prev) => ({ ...prev, [key]: false }));
        })
        .catch((error) => {
          console.error(`Failed to fetch attachment ${id}:`, error);
          setErrorAttachments((prev) => ({ ...prev, [key]: true }));
          setLoadingAttachments((prev) => ({ ...prev, [key]: false }));
        });
    });
  }, [manualHeaders, policyHeaders, manualId]);

  let headerItems: PropertyListItem[] = [];

  // Handle manualHeaders
  if (manualHeaders && manualHeaders.state === 'ENABLED' && manualHeaders.order) {
    manualHeaders.order.forEach((orderItem) => {
      if (orderItem.state === 'DISABLED') {
        return;
      }

      switch (orderItem.type) {
        case 'EFFECTIVE_DATE':
          renderDateHeader('effectiveDate', undefined, manualHeaders.effectiveDateFormat, manualHeaders.autoSetEffectiveDate);
          break;

        case 'REVISED_DATE':
          renderDateHeader('revisedDate', undefined, manualHeaders.revisedDateFormat, manualHeaders.autoSetRevisedDate);
          break;

        case 'AGENCY_LOGO':
          if (manualHeaders.agencyLogo) {
            const attachmentKey = getAttachmentKey('agencyLogo', manualHeaders.agencyLogo);
            const attachment = attachments[attachmentKey];
            const imageUrl = attachment?.url || manualHeaders.agencyLogo;

            headerItems.push({
              label: getHeaderLabel('agencyLogo') || 'Agency Logo',
              value: renderAttachmentImage(attachmentKey, imageUrl, 'Agency Logo'),
            });
          }
          break;

        case 'ISSUING_AUTHORITY':
          const issuingAuthType = manualHeaders.issuingAuthorityType ?? 'SIGNATURE_AND_NAME';
          let issuingAuthValue: React.ReactNode = null;

          if (issuingAuthType === 'SIGNATURE_AND_NAME' || issuingAuthType === 'SIGNATURE_ONLY') {
            if (manualHeaders.issuingAuthoritySignature) {
              const signatureKey = getAttachmentKey('signature', manualHeaders.issuingAuthoritySignature);
              const signatureAttachment = attachments[signatureKey];
              const signatureUrl = signatureAttachment?.url || manualHeaders.issuingAuthoritySignature;

              issuingAuthValue = (
                <Stack direction="column" spacing={1} alignItems="flex-start">
                  {renderAttachmentImage(signatureKey, signatureUrl, 'Signature')}
                  {issuingAuthType === 'SIGNATURE_AND_NAME' && manualHeaders.issuingAuthorityName && (
                    <Typography>{manualHeaders.issuingAuthorityName}</Typography>
                  )}
                </Stack>
              );
            } else if (issuingAuthType === 'SIGNATURE_AND_NAME' && manualHeaders.issuingAuthorityName) {
              issuingAuthValue = manualHeaders.issuingAuthorityName;
            }
          } else if (issuingAuthType === 'NAME_ONLY' && manualHeaders.issuingAuthorityName) {
            issuingAuthValue = manualHeaders.issuingAuthorityName;
          }

          if (issuingAuthValue) {
            headerItems.push({
              label: getHeaderLabel('issuingAuthority'),
              value: issuingAuthValue,
            });
          }
          break;

        case 'CUSTOM1':
        case 'CUSTOM2':
          if (manualHeaders.customFields) {
            const customIndex = orderItem.type === 'CUSTOM1' ? 0 : 1;
            const customField = manualHeaders.customFields[customIndex];

            if (customField && customField.header && customField.value) {
              headerItems.push({
                label: customField.header,
                value: customField.value,
              });
            }
          }
          break;
      }
    });
  }

  // Handle policyHeaders
  if (policyHeaders && policyHeaders.length > 0) {
    policyHeaders.forEach((header) => {
      if (header.state === 'DISABLED') {
        return;
      }

      switch (header.type) {
        case 'EFFECTIVE_DATE':
          renderDateHeader('effectiveDate', header.value, header.dateFormat, header.autoSetDate);
          break;

        case 'REVISED_DATE':
          renderDateHeader('revisedDate', header.value, header.dateFormat, header.autoSetDate);
          break;

        case 'AGENCY_LOGO':
          if (header.agencyLogo) {
            const attachmentKey = getAttachmentKey('agencyLogo', header.agencyLogo);
            const attachment = attachments[attachmentKey];
            const imageUrl = attachment?.url || header.agencyLogo;

            headerItems.push({
              label: getHeaderLabel('agencyLogo') || 'Agency Logo',
              value: renderAttachmentImage(attachmentKey, imageUrl, 'Agency Logo'),
            });
          }
          break;

        case 'ISSUING_AUTHORITY':
          const issuingAuthType = header.issuingAuthorityType ?? 'SIGNATURE_AND_NAME';
          let issuingAuthValue: React.ReactNode = null;

          if (issuingAuthType === 'SIGNATURE_AND_NAME' || issuingAuthType === 'SIGNATURE_ONLY') {
            if (header.issuingAuthoritySignature) {
              const signatureKey = getAttachmentKey('signature', header.issuingAuthoritySignature);
              const signatureAttachment = attachments[signatureKey];
              const signatureUrl = signatureAttachment?.url || header.issuingAuthoritySignature;

              issuingAuthValue = (
                <Stack direction="column" spacing={1} alignItems="flex-start">
                  {renderAttachmentImage(signatureKey, signatureUrl, 'Signature')}
                  {issuingAuthType === 'SIGNATURE_AND_NAME' && header.issuingAuthorityName && (
                    <Typography>{header.issuingAuthorityName}</Typography>
                  )}
                </Stack>
              );
            } else if (issuingAuthType === 'SIGNATURE_AND_NAME' && header.issuingAuthorityName) {
              issuingAuthValue = header.issuingAuthorityName;
            }
          } else if (issuingAuthType === 'NAME_ONLY' && header.issuingAuthorityName) {
            issuingAuthValue = header.issuingAuthorityName;
          }

          if (issuingAuthValue) {
            headerItems.push({
              label: getHeaderLabel('issuingAuthority'),
              value: issuingAuthValue,
            });
          }
          break;

        case 'CUSTOM1':
        case 'CUSTOM2':
          if (header.header && header.value) {
            headerItems.push({
              label: header.header,
              value: header.value,
            });
          }
          break;
      }
    });
  }

  return headerItems.length > 0 ? (
    <Card>
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          py: 3,
          cursor: 'pointer',
          borderBottom: !collapsed ? '1px solid' : 'none',
          borderBottomColor: 'divider',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <Typography variant="h6">{t('policy.stagedManuals.policyHeaders.title')}</Typography>
        <IconButton size="small">
          {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
      </Box>
      <Collapse in={!collapsed}>
        <CardContent sx={{ py: 6, px: { xs: 4, sm: 10 } }}>
          <PropertyList columns={3} items={headerItems} />
        </CardContent>
      </Collapse>
    </Card>
  ) : null;
};

export default PolicyHeaders;